<script setup>
import { computed, ref, watch } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import { useCetakersFlow } from 'dashboard/composables/useCetakersFlow';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import Spinner from 'shared/components/Spinner.vue';
import CreateOrderModal from 'dashboard/components/cetakers-flow/CreateOrderModal.vue';

const { uiSettings, updateUISettings } = useUISettings();
const { isCetakersFlowEnabled } = useCetakersFlow();

const currentChat = useMapGetter('getSelectedChat');
const getters = useStoreGetters();

const cetakersIntegration = computed(() => {
  const apps = getters['integrations/getAppIntegrations'].value;
  return apps?.find(app => app.id === 'cetakers_flow');
});

const apiEndpoint = computed(() => {
  const ep = cetakersIntegration.value?.hooks?.[0]?.settings?.api_endpoint || 'https://app.cetakers.com/fp-cetakers/api/v1/';
  return ep.endsWith('/') ? ep : `${ep}/`;
});
const apiKey = computed(() => cetakersIntegration.value?.hooks?.[0]?.settings?.api_key || '');

const shouldShowPanel = computed(() => {
  return isCetakersFlowEnabled.value && uiSettings.value?.is_cetakers_flow_panel_open;
});

const closePanel = () => {
  updateUISettings({ is_cetakers_flow_panel_open: false });
};

// UI State
const activeTab = ref('invoices'); // invoices, spk, produk
const isLoading = ref(false);
const searchQuery = ref('');
const errorMsg = ref('');
const isCreateOrderModalOpen = ref(false);

// Data State
const customerData = ref(null);
const invoices = ref([]);
const spks = ref([]);

const formatCurrency = (amount) => {
  if (!amount) return '0';
  return Number(amount).toLocaleString('id-ID');
};

const fetchCustomerData = async () => {
  if (!apiKey.value) {
    errorMsg.value = 'API Key belum dikonfigurasi.';
    return;
  }
  if (!currentChat.value?.meta?.sender) {
    errorMsg.value = 'Tidak ada percakapan aktif.';
    customerData.value = null;
    invoices.value = [];
    spks.value = [];
    return;
  }

  const sender = currentChat.value.meta.sender;
  const rawPhone = sender.phone_number || '';
  const email = sender.email || '';
  const senderName = sender.name || '';

  // Clean phone number (strip non-digits)
  const cleanPhone = rawPhone.replace(/\D/g, '');
  const phone08 = cleanPhone ? (cleanPhone.startsWith('62') ? '0' + cleanPhone.slice(2) : cleanPhone) : '';
  const searchQ = phone08 || cleanPhone || email || senderName;

  if (!searchQ) {
    errorMsg.value = 'Pelanggan tidak memiliki nomor telepon atau email.';
    customerData.value = null;
    invoices.value = [];
    spks.value = [];
    return;
  }

  errorMsg.value = '';
  isLoading.value = true;
  customerData.value = null;
  invoices.value = [];
  spks.value = [];

  const headers = { Authorization: `Bearer ${apiKey.value}` };

  try {
    // 1. Direct Parallel Fetch by phone (or search query)
    const phoneParam = phone08 || cleanPhone;
    const queryStr = phoneParam ? `phone=${encodeURIComponent(phoneParam)}` : `search=${encodeURIComponent(searchQ)}`;

    const [custRes, invRes, spkRes] = await Promise.all([
      fetch(`${apiEndpoint.value}customers?${queryStr}&limit=1`, { headers }).catch(() => null),
      fetch(`${apiEndpoint.value}invoices?${queryStr}&limit=20&sort_by=id&order=desc`, { headers }).catch(() => null),
      fetch(`${apiEndpoint.value}spk?${queryStr}&limit=20&sort_by=spk_id&order=desc`, { headers }).catch(() => null),
    ]);

    let custDataArr = [];
    if (custRes && custRes.ok) {
      const custJson = await custRes.json();
      custDataArr = custJson.data || custJson.results || [];
    }

    let invArr = [];
    if (invRes && invRes.ok) {
      const invJson = await invRes.json();
      invArr = invJson.data || [];
    }

    let spkArr = [];
    if (spkRes && spkRes.ok) {
      const spkJson = await spkRes.json();
      spkArr = spkJson.data || [];
    }

    // Fallback: If searching by phone returned no invoices/spk, try searching by senderName
    if (invArr.length === 0 && spkArr.length === 0 && senderName && searchQ !== senderName) {
      const [fallbackInvRes, fallbackSpkRes] = await Promise.all([
        fetch(`${apiEndpoint.value}invoices?search=${encodeURIComponent(senderName)}&limit=20&sort_by=id&order=desc`, { headers }).catch(() => null),
        fetch(`${apiEndpoint.value}spk?search=${encodeURIComponent(senderName)}&limit=20&sort_by=spk_id&order=desc`, { headers }).catch(() => null),
      ]);
      if (fallbackInvRes && fallbackInvRes.ok) {
        const invJson = await fallbackInvRes.json();
        if (invJson.data?.length > 0) invArr = invJson.data;
      }
      if (fallbackSpkRes && fallbackSpkRes.ok) {
        const spkJson = await fallbackSpkRes.json();
        if (spkJson.data?.length > 0) spkArr = spkJson.data;
      }
    }

    invoices.value = invArr;
    spks.value = spkArr;

    if (custDataArr.length > 0) {
      customerData.value = custDataArr[0];
    } else if (invArr.length > 0 || spkArr.length > 0) {
      // Build virtual customer profile from invoice/spk metadata if customer master record is missing
      const firstObj = invArr[0] || spkArr[0];
      customerData.value = {
        cust_individual_id: firstObj.cust_individual_id,
        cust_individual_nama: firstObj.customer_name || firstObj.cust_individual_nama || firstObj.customer?.cust_individual_nama || senderName,
        cust_individual_hp: rawPhone || phone08,
      };
    } else {
      errorMsg.value = 'Pelanggan tidak ditemukan di Cetakers Flow.';
    }
  } catch (e) {
    console.error('Failed to fetch Cetakers Flow customer data', e);
    errorMsg.value = `Gagal mengambil data: ${e.message}`;
  } finally {
    isLoading.value = false;
  }
};

// Re-fetch whenever chat changes while panel is open
watch(() => currentChat.value?.id, (newId) => {
  if (shouldShowPanel.value && newId) {
    searchQuery.value = '';
    fetchCustomerData();
  }
});

// Fetch when panel is opened
watch(shouldShowPanel, (val) => {
  if (val) {
    searchQuery.value = '';
    fetchCustomerData();
  }
});

const filteredInvoices = computed(() => {
  if (!searchQuery.value) return invoices.value;
  const q = searchQuery.value.toLowerCase();
  return invoices.value.filter(i =>
    i.nota_no?.toLowerCase().includes(q) ||
    i.nota_tanggal_create?.toLowerCase().includes(q)
  );
});

const filteredSpks = computed(() => {
  if (!searchQuery.value) return spks.value;
  const q = searchQuery.value.toLowerCase();
  return spks.value.filter(s =>
    s.nota_no?.toLowerCase().includes(q) ||
    s.spk_judul?.toLowerCase().includes(q)
  );
});

</script>

<template>
  <div
    v-if="shouldShowPanel"
    v-on-click-outside="() => closePanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <!-- Header -->
    <header class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-surface-1 shrink-0">
      <div class="flex items-center gap-2">
        <div class="i-lucide-printer text-n-brand size-5"></div>
        <h3 class="text-sm font-semibold text-n-slate-12 m-0">Cetakers Flow</h3>
      </div>
      <div class="flex items-center gap-1">
        <button
          @click="isCreateOrderModalOpen = true"
          class="px-2.5 py-1.5 text-xs font-semibold text-white bg-n-brand rounded-lg hover:opacity-90 transition-colors inline-flex items-center gap-1 cursor-pointer shadow-sm"
          title="Buat Order SPK Baru"
        >
          <span class="i-lucide-plus size-3.5 text-white"></span>
          Order Baru
        </button>
        <Button
          ghost
          sm
          icon="i-lucide-x"
          class="text-n-slate-10 hover:text-n-slate-12"
          @click="closePanel"
        />
      </div>
    </header>

    <!-- Customer Info Strip (if found) -->
    <div v-if="customerData" class="flex items-center gap-2 px-4 py-2 bg-n-brand-1 border-b border-n-brand-3 shrink-0">
      <div class="size-7 rounded-full bg-n-brand-3 text-n-brand-11 flex items-center justify-center text-xs font-bold uppercase shrink-0">
        {{ (customerData.cust_individual_nama || customerData.cust_individual_perusahaan || '?').charAt(0) }}
      </div>
      <div class="flex flex-col min-w-0">
        <span class="text-xs font-semibold text-n-brand-11 truncate">
          {{ customerData.cust_individual_nama || customerData.cust_individual_perusahaan || 'Pelanggan' }}
        </span>
        <span class="text-[10px] text-n-brand-9">
          ID: {{ customerData.cust_individual_id || customerData.id }}
        </span>
      </div>
      <button
        class="ml-auto text-n-brand-9 hover:text-n-brand-11 p-1 rounded hover:bg-n-brand-2 transition-colors outline-none"
        @click="fetchCustomerData"
        title="Refresh data"
      >
        <span class="i-lucide-refresh-cw size-3.5"></span>
      </button>
    </div>

    <!-- Search Bar -->
    <div class="px-3 py-2.5 border-b border-n-weak bg-n-surface-1 shrink-0">
      <div class="relative flex items-center w-full">
        <span class="absolute left-3 i-lucide-search text-n-slate-9 size-3.5 pointer-events-none"></span>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Cari SPK, Invoice..."
          class="w-full pl-8 pr-3 py-2 text-xs bg-n-surface-2 border border-n-weak rounded-lg text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand-6 focus:border-n-brand-6 transition-all"
        />
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex items-stretch border-b border-n-weak bg-n-surface-1 shrink-0">
      <button
        @click="activeTab = 'invoices'"
        class="flex-1 py-2.5 text-xs font-medium outline-none transition-colors relative"
        :class="activeTab === 'invoices'
          ? 'text-n-brand-11 bg-n-brand-1'
          : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'"
      >
        <span
          v-if="activeTab === 'invoices'"
          class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand-9 rounded-t"
        ></span>
        Invoices ({{ invoices.length }})
      </button>
      <button
        @click="activeTab = 'spk'"
        class="flex-1 py-2.5 text-xs font-medium outline-none transition-colors relative"
        :class="activeTab === 'spk'
          ? 'text-n-brand-11 bg-n-brand-1'
          : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'"
      >
        <span
          v-if="activeTab === 'spk'"
          class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand-9 rounded-t"
        ></span>
        SPK ({{ spks.length }})
      </button>
      <button
        @click="activeTab = 'produk'"
        class="flex-1 py-2.5 text-xs font-medium outline-none transition-colors relative"
        :class="activeTab === 'produk'
          ? 'text-n-brand-11 bg-n-brand-1'
          : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'"
      >
        <span
          v-if="activeTab === 'produk'"
          class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand-9 rounded-t"
        ></span>
        Produk
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex-1 flex flex-col justify-center items-center gap-3">
      <Spinner size="lg" />
      <span class="text-xs text-n-slate-10">Mengambil data pelanggan...</span>
    </div>

    <!-- Dynamic Content -->
    <div v-else class="flex-1 overflow-y-auto p-3 bg-n-surface-1">

      <!-- Error Message -->
      <div v-if="errorMsg && activeTab !== 'produk'" class="flex flex-col items-center justify-center text-center mt-8 px-4">
        <div class="w-12 h-12 bg-n-amber-1 rounded-full flex items-center justify-center mb-3 border border-n-amber-3">
          <span class="i-lucide-alert-circle text-n-amber-9 size-6"></span>
        </div>
        <p class="text-xs text-n-slate-10 mb-4">{{ errorMsg }}</p>
        <Button color="brand" sm icon="i-lucide-refresh-cw" @click="fetchCustomerData">Coba Lagi</Button>
      </div>

      <!-- INVOICES TAB -->
      <template v-else-if="activeTab === 'invoices'">
        <div v-if="filteredInvoices.length > 0" class="flex flex-col gap-2">
          <div
            v-for="(inv, idx) in filteredInvoices"
            :key="idx"
            class="p-3 bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl shadow-sm hover:shadow-md transition-shadow"
          >
            <div class="flex justify-between items-start mb-1.5">
              <span class="font-semibold text-sm text-n-slate-12">{{ inv.nota_no }}</span>
              <span
                :class="(inv.nota_status_pembayaran === 1 || inv.nota_status_pembayaran === '1')
                  ? 'bg-n-green-1 text-n-green-11 border-n-green-3'
                  : 'bg-n-amber-1 text-n-amber-11 border-n-amber-3'"
                class="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border"
              >
                {{ (inv.nota_status_pembayaran === 1 || inv.nota_status_pembayaran === '1') ? 'Lunas' : 'Belum Lunas' }}
              </span>
            </div>
            <div class="text-xs text-n-slate-10 mb-2 flex items-center gap-1">
              <span class="i-lucide-calendar size-3"></span>
              {{ inv.nota_tanggal_create || '-' }}
            </div>
            <div class="flex justify-between items-center text-sm pt-2 border-t border-n-weak">
              <span class="text-n-slate-11 text-xs">Total Tagihan</span>
              <span class="font-bold text-n-slate-12 text-sm">
                Rp {{ formatCurrency(inv.nota_total_harga || inv.nota_total) }}
              </span>
            </div>
          </div>
        </div>
        <!-- Empty Invoices -->
        <div v-else class="flex flex-col items-center justify-center text-center mt-10 px-4">
          <div class="w-12 h-12 bg-n-alpha-1 rounded-full flex items-center justify-center mb-3 border border-n-weak">
            <span class="i-lucide-file-text text-n-slate-8 size-6"></span>
          </div>
          <h4 class="text-sm font-medium text-n-slate-12 mb-1">Belum ada Invoice</h4>
          <p class="text-xs text-n-slate-10 mb-4">
            Pelanggan ini belum memiliki riwayat Invoice.
          </p>
        </div>
      </template>

      <!-- SPK TAB -->
      <template v-else-if="activeTab === 'spk'">
        <div v-if="filteredSpks.length > 0" class="flex flex-col gap-2">
          <div
            v-for="(spk, idx) in filteredSpks"
            :key="idx"
            class="p-3 bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl shadow-sm hover:shadow-md transition-shadow"
          >
            <div class="flex flex-col mb-2">
              <span class="text-xs text-n-slate-10 font-mono">{{ spk.nota_no || `SPK #${spk.spk_id}` }}</span>
              <span class="font-semibold text-sm text-n-slate-12 mt-1 leading-snug">{{ spk.spk_judul || 'Tanpa Judul' }}</span>
            </div>
            <div class="flex justify-between items-center pt-2 border-t border-n-weak">
              <div class="text-xs text-n-slate-10 flex items-center gap-1">
                <span class="i-lucide-calendar size-3"></span>
                {{ spk.spk_tanggal || '-' }}
              </div>
              <div class="flex items-center gap-2">
                <div v-if="spk.spk_status_pengerjaan" class="text-[10px] bg-n-brand-1 border border-n-brand-3 px-2 py-0.5 rounded-md text-n-brand-11 font-semibold uppercase">
                  {{ spk.spk_status_pengerjaan }}
                </div>
                <div class="text-xs bg-n-surface-2 px-2 py-0.5 rounded-md text-n-slate-11 font-medium border border-n-weak">
                  Qty: {{ spk.spk_jumlah || 0 }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center text-center mt-10 px-4">
          <div class="w-12 h-12 bg-n-alpha-1 rounded-full flex items-center justify-center mb-3 border border-n-weak">
            <span class="i-lucide-settings text-n-slate-8 size-6"></span>
          </div>
          <h4 class="text-sm font-medium text-n-slate-12 mb-1">Belum ada SPK</h4>
          <p class="text-xs text-n-slate-10">Pelanggan ini belum memiliki riwayat SPK.</p>
        </div>
      </template>

      <!-- PRODUK TAB -->
      <template v-else-if="activeTab === 'produk'">
        <div class="flex flex-col items-center justify-center text-center mt-10 px-4">
          <div class="w-12 h-12 bg-n-brand-1 rounded-full flex items-center justify-center mb-3 border border-n-brand-3">
            <span class="i-lucide-box text-n-brand-9 size-6"></span>
          </div>
          <h4 class="text-sm font-medium text-n-slate-12 mb-1">Coming Soon</h4>
          <p class="text-xs text-n-slate-10 px-4">
            Fitur katalog Produk Cetakers Flow akan segera hadir.
          </p>
        </div>
      </template>

    </div>

    <!-- Create Order Modal -->
    <CreateOrderModal
      :show="isCreateOrderModalOpen"
      :prefilled-customer-id="customerData?.cust_individual_id || customerData?.id"
      :prefilled-customer-name="customerData?.cust_individual_nama || customerData?.cust_individual_perusahaan || ''"
      @close="isCreateOrderModalOpen = false"
      @success="fetchCustomerData"
    />
  </div>
</template>
